/**
 * @voltxampere/engine — Main-thread Worker client (Sprint 9)
 *
 * Promise-based wrapper around the worker's postMessage surface.
 * Each `send*` method correlates its request id with the worker
 * response and returns a typed Promise. Streaming and progress
 * carry through event-style listeners so consumers can render
 * frames as they arrive.
 *
 * The client is transport-agnostic — it accepts any object that
 * implements the minimal `WorkerLike` interface. Production
 * usage passes a browser `Worker`; tests pass an in-process
 * `FakeWorker` defined in the test file.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { randomUUID } from "./uuid.js";
export class EngineWorkerClientError extends Error {
    code;
    constructor(error) {
        super(error.message);
        this.name = "EngineWorkerClientError";
        this.code = error.code;
    }
}
/**
 * Promise-based client wrapping a Web Worker (or compatible
 * transport). Lifecycle:
 *
 *   1. `new EngineWorkerClient(worker)` — attach listeners.
 *   2. `await client.init(circuit)` — loads the circuit.
 *   3. `await client.runAnalysis({ kind: "DC" })` — single-shot.
 *   4. `client.startStream(60, onFrame)` — live mode.
 *   5. `await client.destroy()` — graceful shutdown.
 */
export class EngineWorkerClient {
    worker;
    pending = new Map();
    frameListeners = new Set();
    constructor(worker) {
        this.worker = worker;
        this.worker.onmessage = (ev) => {
            this.dispatch(ev.data);
        };
        this.worker.onerror = (ev) => {
            this.onWorkerError(ev);
        };
    }
    async init(circuit, options) {
        await this.request({
            type: "init",
            id: randomUUID(),
            circuit,
            ...(options !== undefined ? { options } : {}),
        });
    }
    async updateCircuit(circuit) {
        await this.request({ type: "updateCircuit", id: randomUUID(), circuit });
    }
    async runAnalysis(analysis, opts) {
        const id = randomUUID();
        const resp = await this.request({ type: "runAnalysis", id, analysis }, opts?.onProgress);
        if (resp.type !== "analysisResult") {
            throw new EngineWorkerClientError({
                code: "UNKNOWN",
                message: `Expected analysisResult, got ${resp.type}`,
            });
        }
        return resp.result;
    }
    async runAssertions(analysisKind) {
        const id = randomUUID();
        const resp = await this.request({
            type: "runAssertions",
            id,
            analysis: analysisKind,
        });
        if (resp.type !== "assertionResult") {
            throw new EngineWorkerClientError({
                code: "UNKNOWN",
                message: `Expected assertionResult, got ${resp.type}`,
            });
        }
        return resp.results;
    }
    /** Start a live-mode stream. Returns a stop function. */
    startStream(fps, onFrame) {
        this.frameListeners.add(onFrame);
        const id = randomUUID();
        // Fire-and-forget startStream; the worker begins posting
        // frames immediately. We do NOT await the handshake here
        // because the handler emits the first frame synchronously
        // before any "ready"-like response.
        this.worker.postMessage({ type: "startStream", id, fps });
        return async () => {
            this.frameListeners.delete(onFrame);
            await this.request({ type: "stopStream", id: randomUUID() });
        };
    }
    async cancel(targetId) {
        await this.request({ type: "cancel", id: randomUUID(), targetId });
    }
    async destroy() {
        try {
            await this.request({ type: "destroy", id: randomUUID() });
        }
        finally {
            this.worker.terminate();
        }
    }
    // -----------------------------------------------------------
    // Private
    // -----------------------------------------------------------
    request(req, onProgress) {
        return new Promise((resolve, reject) => {
            this.pending.set(req.id, {
                resolve,
                reject,
                ...(onProgress !== undefined ? { onProgress } : {}),
            });
            this.worker.postMessage(req);
        });
    }
    dispatch(resp) {
        switch (resp.type) {
            case "ready":
            case "analysisResult":
            case "assertionResult":
            case "cancelled":
            // Sprint 10.0 (B-P1-01 closure): `streamStopped` is the
            // dedicated reply for `stopStream` requests. The client's
            // `startStream` returns a stop-function that internally awaits
            // `stopStream`, so this case must resolve the matching pending
            // request the same way `destroyed` does for `destroy`.
            case "streamStopped":
            case "destroyed": {
                const entry = this.pending.get(resp.id);
                if (entry) {
                    entry.resolve(resp);
                    this.pending.delete(resp.id);
                }
                return;
            }
            case "progress": {
                const entry = this.pending.get(resp.id);
                entry?.onProgress?.(resp.percent, resp.stage);
                return;
            }
            case "frame":
                for (const l of this.frameListeners)
                    l(resp.state);
                return;
            case "error": {
                const entry = this.pending.get(resp.id);
                if (entry) {
                    entry.reject(new EngineWorkerClientError(resp.error));
                    this.pending.delete(resp.id);
                }
                else {
                    // Unsolicited error (e.g. STREAM_SOLVE_FAIL). We
                    // surface it via the frame listeners as a best-effort
                    // "warning" — listeners ignore frames with empty
                    // nodeVoltages, so the signal stays addressable without
                    // crashing the Promise layer.
                    for (const l of this.frameListeners) {
                        l({
                            timestamp: 0,
                            nodeVoltages: {},
                            branchCurrents: {},
                            converged: false,
                            iterations: 0,
                            warnings: [resp.error.message],
                        });
                    }
                }
                return;
            }
        }
    }
    onWorkerError(ev) {
        const err = new EngineWorkerClientError({
            code: "UNKNOWN",
            message: ev instanceof Error ? ev.message : "worker errored",
        });
        for (const entry of this.pending.values())
            entry.reject(err);
        this.pending.clear();
    }
}
//# sourceMappingURL=client.js.map